import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { SectionCard, InfoRow, formatDate, formatINR, formatDateTime } from "../../UI/index";
import { useAdminFillIntake, useApproveEstimate, useUpdateCaseStatus,useHandleClaimForm } from "../../../../hooks/useWorkshopQueries";  
import { STATUSES_PAST_INTAKE, STATUSES_PAST_ESTIMATE_APPROVED, STATUSES_PAST_CLAIM_UPLOADED, STATUSES_PAST_CUSTOMER_SIGNED, STATUSES_PAST_CLAIM_APPROVED, STATUSES_PAST_CLAIM_SENT } from "./WorkshopConstants";
import {StepLabel,StepCard, StepHeader,} from "./WorkshopTabHelpers";

function SaveRow({ onSave, onCancel, saving, disabled, label = "Save" }) {
  return (
    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 10 }}>
      <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={onCancel} disabled={saving}>Cancel</button>
      <button className="wd-btn wd-btn-primary wd-btn-sm" onClick={onSave} disabled={saving || disabled}>
        {saving ? "Saving…" : label}
      </button>
    </div>
  );
}

function PdfPill({ label, url, onRemove }) {
  const rawName = url ? url.split("/").pop().split("?")[0] : null;

  const displayName = rawName && rawName.length > 28
    ? rawName.slice(0, 12) + "…" + rawName.slice(-8)
    : rawName || label;

  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 8,
      padding: "6px 12px", borderRadius: 8,
      background: "rgba(59,130,246,.07)", border: "1px solid rgba(59,130,246,.2)",
    }}>
      {/* PDF icon */}
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--wd-blue)" strokeWidth="2" style={{ flexShrink: 0 }}>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
        <line x1="16" y1="13" x2="8" y2="13"/>
        <line x1="16" y1="17" x2="8" y2="17"/>
        <polyline points="10 9 9 9 8 9"/>
      </svg>
 
      {/* Label */}
      <div style={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
        <span style={{
          fontSize: 11, fontWeight: 600, color: "var(--wd-blue)",
          whiteSpace: "nowrap",
        }}>
          {label}
        </span>
        <span style={{
          fontSize: 10, color: "var(--wd-t3)",
          fontFamily: "var(--wd-mono)",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          maxWidth: 160,
        }}>
          {displayName}
        </span>
      </div>
 
      {/* View button */}
      {url && (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontSize: 11, fontWeight: 600,
            padding: "3px 8px", borderRadius: 6,
            background: "rgba(59,130,246,.15)",
            color: "var(--wd-blue)",
            border: "1px solid rgba(59,130,246,.25)",
            textDecoration: "none",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
          onClick={e => e.stopPropagation()}
        >
          View PDF ↗
        </a>
      )}
 
      {/* Remove button (only in edit mode) */}
      {onRemove && (
        <button
          onClick={e => { e.stopPropagation(); onRemove(); }}
          style={{
            background: "none", border: "none", cursor: "pointer",
            color: "var(--wd-t3)", fontSize: 14, padding: 0,
            lineHeight: 1, flexShrink: 0,
          }}
          title="Remove"
        >
          ✕
        </button>
      )}
    </div>
  );
} 
 
// Compact PDF upload drop zone
function PdfUploadZone({ label, file, existingUrl, onFile, onClear, dragId }) {
  const [dragOver, setDragOver] = useState(false);
  const hasFile = file || existingUrl;
 
  if (hasFile) {
    return (
      <div>
        <StepLabel>{label}</StepLabel>
        <PdfPill
          label={label}
          url={file ? URL.createObjectURL(file) : existingUrl}
          onRemove={onClear}
        />
      </div>
    );
  }
 
  return (
    <div>
      <StepLabel>{label} *</StepLabel>
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => {
          e.preventDefault(); setDragOver(false);
          const f = e.dataTransfer.files[0];
          if (f?.type === "application/pdf") onFile(f);
        }}
        onClick={() => document.getElementById(dragId).click()}
        style={{
          border: `1.5px dashed ${dragOver ? "var(--wd-blue)" : "var(--wd-border-md)"}`,
          borderRadius: 8, padding: "10px 12px",
          background: dragOver ? "rgba(59,130,246,.05)" : "var(--wd-hover)",
          cursor: "pointer", transition: "all .15s",
          display: "flex", alignItems: "center", gap: 8,
        }}
      >
        <input id={dragId} type="file" accept="application/pdf" style={{ display: "none" }}
          onChange={e => { const f = e.target.files[0]; if (f?.type === "application/pdf") onFile(f); }} />
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--wd-t3)" strokeWidth="2">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
        </svg>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: "var(--wd-t2)" }}>Drop PDF or click to browse</div>
          <div style={{ fontSize: 10, color: "var(--wd-t3)" }}>{label} — PDF only</div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STEP A — Waiting for Workshop Estimate
// Workshop fills: inventory checked, entry date/time, estimate amount
// Admin sees a read-only waiting state until workshop submits, then approves
// ─────────────────────────────────────────────────────────────────────────────
export function StepWorkshopEstimate({ caseInfo, workshopInfo, onRefetch }) {
  const fillIntake = useAdminFillIntake();
  const approveEst = useApproveEstimate();
 
  const [localIntake, setLocalIntake] = useState(() => workshopInfo || {});
 
  // tracks whether admin has just saved — prevents useEffect from overwriting
  const justSavedRef = useRef(false);
 
   const intakeSubmitted =
    localIntake?.intakeSubmitted ||
    !!(localIntake?.entryDate && localIntake?.estimateAmount) ||
    STATUSES_PAST_INTAKE.includes(caseInfo.currentStatus);
 
  // estimateApproved: stays true for ALL statuses past this point
  const estimateApproved =
    !!caseInfo.estimateApprovedDate ||
    caseInfo.currentStatus === "ESTIMATE_APPROVED" ||
    STATUSES_PAST_ESTIMATE_APPROVED.includes(caseInfo.currentStatus);
 
  const [open,          setOpen]          = useState(false);
  const [approving,     setApproving]     = useState(false);
  const [saving,        setSaving]        = useState(false);
  const [formEntry,     setFormEntry]     = useState(workshopInfo?.entryDate?.slice?.(0, 16) || "");
  const [formEstimate,  setFormEstimate]  = useState(workshopInfo?.estimateAmount || "");
  const [inventoryFile, setInventoryFile] = useState(null);
  const [estimateFile,  setEstimateFile]  = useState(null);
 
  useEffect(() => {
    if (!workshopInfo) return;
 
    if (justSavedRef.current) {
      if (workshopInfo?.intakeSubmitted || (workshopInfo?.entryDate && workshopInfo?.estimateAmount)) {
        justSavedRef.current = false;
        setLocalIntake(workshopInfo);
      }
    } else {
      setLocalIntake(workshopInfo);
    }
 
    if (!open) {
      setFormEntry(workshopInfo?.entryDate?.slice?.(0, 16) || "");
      setFormEstimate(workshopInfo?.estimateAmount || "");
    }
  }, [workshopInfo, open]);
 
  const handleApprove = useCallback(async () => {
    setApproving(true);
    try {
      await approveEst.mutateAsync({ caseId: caseInfo.accidentCaseNo, approvedDate: new Date().toISOString() });
      onRefetch?.({ estimateApprovedDate: new Date().toISOString(), currentStatus: "ESTIMATE_APPROVED" });
    } catch (e) { console.error(e); }
    setApproving(false);
  }, [caseInfo.accidentCaseNo, approveEst, onRefetch]);
 
  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("workshopId",     workshopInfo?.workshopId || caseInfo.workshopId || "");
      formData.append("entryDate",      formEntry     || "");
      formData.append("estimateAmount", formEstimate  || "");
      if (inventoryFile) formData.append("inventoryPdf", inventoryFile);
      if (estimateFile)  formData.append("estimatePdf",  estimateFile);
 
      const result = await fillIntake.mutateAsync({
        caseId: caseInfo.accidentCaseNo,
        formData,
      });
      // Mark that we've saved locally — useEffect will not overwrite this
      justSavedRef.current = true;
 
      // Update local state immediately — this is what the UI reads
     setLocalIntake(prev => ({
        ...prev,
        entryDate:       formEntry,
        estimateAmount:  Number(formEstimate),
        intakeSubmitted: true,
        inventoryPdf:    result?.inventoryPdf || prev?.inventoryPdf || null,
        estimatePdf:     result?.estimatePdf  || prev?.estimatePdf  || null,
      }));
 
      onRefetch?.({
        vehicleEntryDate: formEntry,
        estimateAmount:   Number(formEstimate),
        currentStatus:    "WORKSHOP_ESTIMATE",
      }, {
        entryDate:      formEntry,
        estimateAmount: Number(formEstimate),
        intakeSubmitted:true,
        inventoryPdf:   result?.inventoryPdf || localIntake?.inventoryPdf || workshopInfo?.inventoryPdf || null,
        estimatePdf:    result?.estimatePdf  || localIntake?.estimatePdf  || workshopInfo?.estimatePdf  || null,
        inventoryChecked: localIntake?.inventoryChecked || workshopInfo?.inventoryChecked || false,
      });
 
      setOpen(false);
      setInventoryFile(null);
      setEstimateFile(null);
    } catch (e) { console.error(e); }
    setSaving(false);
  }, [formEntry, formEstimate, inventoryFile, estimateFile, workshopInfo, caseInfo, fillIntake, onRefetch]);
 
  const borderColor = estimateApproved ? "var(--wd-green)" : intakeSubmitted ? "var(--wd-yellow)" : "var(--wd-blue)";
 
  return (
    <div style={{
      borderRadius: 10, overflow: "hidden",
      border: `1px solid ${borderColor}44`,
      borderLeft: `3px solid ${borderColor}`,
      background: estimateApproved ? "rgba(34,197,94,.04)" : intakeSubmitted ? "rgba(234,179,8,.04)" : "rgba(59,130,246,.04)",
      padding: "14px 16px",
    }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 16 }}>
            {estimateApproved ? "✅" : intakeSubmitted ? "📋" : "⏳"}
          </span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--wd-t1)" }}>Workshop intake &amp; estimate</div>
            <div style={{ fontSize: 11, color: "var(--wd-t3)", marginTop: 1 }}>
              {estimateApproved
                ? `Approved — ₹${Number(localIntake?.estimateAmount || 0).toLocaleString("en-IN")} · ${new Date(caseInfo.estimateApprovedDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}`
                : intakeSubmitted
                ? "Workshop submitted details — pending admin approval"
                : "Waiting for workshop to submit intake"}
            </div>
          </div>
        </div>
        <span style={{
          fontSize: 10, fontWeight: 700, padding: "2px 9px", borderRadius: 20, flexShrink: 0,
          background: estimateApproved ? "rgba(34,197,94,.12)" : intakeSubmitted ? "rgba(234,179,8,.12)" : "rgba(59,130,246,.12)",
          color: estimateApproved ? "var(--wd-green)" : intakeSubmitted ? "var(--wd-yellow)" : "var(--wd-blue)",
          border: `1px solid ${estimateApproved ? "rgba(34,197,94,.25)" : intakeSubmitted ? "rgba(234,179,8,.25)" : "rgba(59,130,246,.25)"}`,
          textTransform: "uppercase", letterSpacing: ".04em",
        }}>
          {estimateApproved ? "Approved" : intakeSubmitted ? "Review required" : "Waiting"}
        </span>
      </div>
 
      {/* Submitted details */}
      {intakeSubmitted && (
        <div style={{
          marginTop: 12, padding: "10px 12px", borderRadius: 8,
          background: "var(--wd-hover)", border: "1px solid var(--wd-border)",
        }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 10 }}>
            <div>
              <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--wd-t3)", marginBottom: 3 }}>Vehicle entry</div>
              <div style={{ fontSize: 12, fontWeight: 500, color: "var(--wd-t1)" }}>  {formatDateTime(localIntake?.entryDate || workshopInfo?.entryDate)}</div>
            </div>
            <div>
              <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--wd-t3)", marginBottom: 3 }}>Estimate amount</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--wd-blue)" }}> {formatINR(localIntake?.estimateAmount || workshopInfo?.estimateAmount)}</div>
            </div>
            <div>
              <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--wd-t3)", marginBottom: 3 }}>Inventory check</div>
              <div style={{ fontSize: 12, fontWeight: 500, color: localIntake?.inventoryChecked || workshopInfo?.inventoryChecked ? "var(--wd-green)" : "var(--wd-yellow)" }}>
                {(localIntake?.inventoryChecked || workshopInfo?.inventoryChecked)  ? "✓ Done" : "⚠ Partial"}
              </div>
            </div>
          </div>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 2 }}>
            {/* Inventory PDF */}
            <div>
              <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--wd-t3)", marginBottom: 5 }}>
                Inventory PDF
              </div>
              {(localIntake?.inventoryPdf || workshopInfo?.inventoryPdf) ? (
                <PdfPill
                  label="Inventory check"
                  url={localIntake?.inventoryPdf || workshopInfo?.inventoryPdf}
                />
              ) : (
                <span style={{ fontSize: 11, color: "var(--wd-t3)", fontStyle: "italic" }}>
                  Not uploaded
                </span>
              )}
            </div>
 
            {/* Estimate PDF */}
            <div>
              <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--wd-t3)", marginBottom: 5 }}>
                Estimate PDF
              </div>
              {(localIntake?.estimatePdf || workshopInfo?.estimatePdf) ? (
                <PdfPill
                  label="Estimate"
                  url={localIntake?.estimatePdf || workshopInfo?.estimatePdf}
                />
              ) : (
                <span style={{ fontSize: 11, color: "var(--wd-t3)", fontStyle: "italic" }}>
                  Not uploaded
                </span>
              )}
            </div>
          </div>
        </div>
      )}
 
      {/* Approve action */}
      {intakeSubmitted && !estimateApproved && (
        <div style={{
          marginTop: 10, padding: "8px 12px", borderRadius: 8,
          background: "rgba(249,115,22,.06)", border: "1px solid rgba(249,115,22,.18)",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
        }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--wd-orange)" }}>Admin approval required</div>
            <div style={{ fontSize: 10, color: "var(--wd-t3)", marginTop: 1 }}>Review estimate &amp; PDFs above, then approve to proceed</div>
          </div>
          <button className="wd-btn wd-btn-primary wd-btn-sm" style={{ whiteSpace: "nowrap" }}
            onClick={handleApprove} disabled={approving}>
            {approving ? "Approving…" : "✓ Approve estimate"}
          </button>
        </div>
      )}
 
      {/* Workshop hasn't submitted */}
      {!intakeSubmitted && !open && (
        <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center" }}>
          <div style={{
            flex: 1, padding: "7px 10px", borderRadius: 7, fontSize: 11,
            background: "rgba(59,130,246,.05)", border: "1px dashed rgba(59,130,246,.25)",
            color: "var(--wd-t2)", lineHeight: 1.5,
          }}>
            Workshop needs to submit entry date, inventory PDF &amp; estimate PDF from their portal.
            You can also fill this manually.
          </div>
          <button className="wd-btn wd-btn-ghost wd-btn-sm" style={{ whiteSpace: "nowrap" }}
            onClick={() => setOpen(true)}>
            ✏️ Fill manually
          </button>
        </div>
      )}
 
      {/* Admin fill form */}
      {!intakeSubmitted && open && (
        <div style={{
          marginTop: 12, paddingTop: 12,
          borderTop: "1px dashed var(--wd-border)",
          display: "flex", flexDirection: "column", gap: 12,
        }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <StepLabel>Vehicle entry date &amp; time *</StepLabel>
              <input className="wd-input" type="datetime-local" value={formEntry}
                onChange={e => setFormEntry(e.target.value)} />
            </div>
            <div>
              <StepLabel>Estimate amount (₹) *</StepLabel>
              <input className="wd-input" type="number" placeholder="e.g. 125000"
                value={formEstimate} onChange={e => setFormEstimate(e.target.value)} />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <PdfUploadZone label="Inventory check PDF" file={inventoryFile}
              existingUrl={localIntake?.inventoryPdf} onFile={setInventoryFile}
              onClear={() => setInventoryFile(null)} dragId="inventory-pdf-input" />
            <PdfUploadZone label="Estimate PDF" file={estimateFile}
              existingUrl={localIntake?.estimatePdf} onFile={setEstimateFile}
              onClear={() => setEstimateFile(null)} dragId="estimate-pdf-input" />
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={() => setOpen(false)} disabled={saving}>Cancel</button>
            <button className="wd-btn wd-btn-primary wd-btn-sm" onClick={handleSave}
              disabled={saving || !formEntry || !formEstimate}>
              {saving ? "Saving…" : "Save intake"}
            </button>
          </div>
        </div>
      )}
 
      {/* Edit link when submitted but not approved */}
      {intakeSubmitted && !estimateApproved && (
        <div style={{ marginTop: 6, textAlign: "right" }}>
          <button className="wd-btn wd-btn-ghost wd-btn-sm" style={{ fontSize: 10 }}
            onClick={() => setOpen(v => !v)}>
            {open ? "Cancel edit" : "✏️ Edit / replace PDFs"}
          </button>
        </div>
      )}
 
      {/* Edit form when already submitted */}
      {intakeSubmitted && !estimateApproved && open && (
        <div style={{
          marginTop: 10, paddingTop: 10,
          borderTop: "1px dashed var(--wd-border)",
          display: "flex", flexDirection: "column", gap: 10,
        }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <PdfUploadZone label="Replace inventory PDF" file={inventoryFile} existingUrl={null}
              onFile={setInventoryFile} onClear={() => setInventoryFile(null)} dragId="inventory-pdf-replace" />
            <PdfUploadZone label="Replace estimate PDF" file={estimateFile} existingUrl={null}
              onFile={setEstimateFile} onClear={() => setEstimateFile(null)} dragId="estimate-pdf-replace" />
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={() => setOpen(false)} disabled={saving}>Cancel</button>
            <button className="wd-btn wd-btn-primary wd-btn-sm" onClick={handleSave}
              disabled={saving || (!inventoryFile && !estimateFile)}>
              {saving ? "Saving…" : "Update PDFs"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// STEP B — Claim Form (Draft → Customer Signs → Admin Approves → Send to Insurer)
// ─────────────────────────────────────────────────────────────────────────────
export function StepClaimForm({ caseInfo, workshopInfo, insuranceDetails, onRefetch }) {
  const updateCaseStatus = useUpdateCaseStatus();
  const handleClaimForm  = useHandleClaimForm();

  // ── Digital insurance — initialised from caseInfo so it survives re-mounts.
  // When CLAIM_SUBMITTED is reached via digital insurance toggle, we store that
  // in caseInfo.isDigitalInsurance (if your backend supports it) OR we derive it
  // from a local flag seeded by whether currentStatus is already CLAIM_SUBMITTED
  // AND no actual claim form docs exist (meaning it was skipped, not completed).
  const wasSkippedViaDigital =
    (caseInfo.currentStatus === "CLAIM_SUBMITTED" ||
     STATUSES_PAST_CLAIM_SENT.includes(caseInfo.currentStatus)) &&
    !caseInfo.claimFormDoc &&
    !caseInfo.claimFormSignedDoc;

  const [digitalInsurance, setDigitalInsurance] = useState(() => wasSkippedViaDigital);

  // Each flag includes all downstream statuses so it stays true once passed
  const claimFormUploaded =
    !!caseInfo.claimFormDoc ||
    STATUSES_PAST_CLAIM_UPLOADED.includes(caseInfo.currentStatus) ||
    caseInfo.currentStatus === "CLAIM_FORM_SENT_TO_CUSTOMER";

  const customerSigned =
    !!caseInfo.claimFormSignedDoc ||
    caseInfo.currentStatus === "CLAIM_FORM_SIGNED" ||
    STATUSES_PAST_CUSTOMER_SIGNED.includes(caseInfo.currentStatus);

  const claimFormApproved =
    !!caseInfo.claimFormApprovedDate ||
    caseInfo.currentStatus === "CLAIM_FORM_APPROVED" ||
    STATUSES_PAST_CLAIM_APPROVED.includes(caseInfo.currentStatus);

  const sentToInsurer =
    !!caseInfo.claimFormSentDate ||
    caseInfo.currentStatus === "CLAIM_SUBMITTED" ||
    STATUSES_PAST_CLAIM_SENT.includes(caseInfo.currentStatus);

  const effectivelySentToInsurer = sentToInsurer || digitalInsurance;

  // Use "skipped" state when digital insurance is active so the card
  // shows a purple left border instead of the green "done" treatment
  const state = digitalInsurance        ? "skipped"
    : sentToInsurer                     ? "done"
    : claimFormApproved                 ? "info"
    : customerSigned                    ? "warning"
    : "active";

  const [panel,          setPanel]          = useState(null);
  const [saving,         setSaving]         = useState(false);
  const [draftFile,      setDraftFile]      = useState(null);
  const [dragOver,       setDragOver]       = useState(false);
  const [mailing,        setMailing]        = useState(false);
  const [mailed,         setMailed]         = useState(false);
  const [signedFile,     setSignedFile]     = useState(null);
  const [signedDragOver, setSignedDragOver] = useState(false);
  const [skipped,        setSkipped]        = useState(false);

  const statusSteps = [
    { key: "draft",         label: "Draft uploaded",   done: claimFormUploaded, skipped: digitalInsurance },
    { key: "awaiting_sign", label: "Sent to customer", done: claimFormUploaded, skipped: digitalInsurance },
    { key: "signed",        label: "Customer signed",  done: customerSigned,    skipped: digitalInsurance },
    { key: "approved",      label: "Admin approved",   done: claimFormApproved, skipped: digitalInsurance },
    { key: "sent",          label: "Sent to insurer",  done: sentToInsurer,     skipped: digitalInsurance },
  ];

  // ── Handlers (unchanged) ─────────────────────────────────────────────────
  const handleUploadDraft = useCallback(async () => {
    if (!draftFile) return;
    setSaving(true);
    try {
      const result = await handleClaimForm.mutateAsync({
        caseId: caseInfo.accidentCaseNo,
        type:   "DRAFT",
        file:   draftFile,
      });
      onRefetch?.({
        claimFormDoc:  result?.fileUrl || draftFile.name,
        currentStatus: "CLAIM_FORM_SENT_TO_CUSTOMER",
      });
      setPanel(null);
      setDraftFile(null);
    } catch (e) { console.error(e); }
    setSaving(false);
  }, [draftFile, caseInfo.accidentCaseNo, handleClaimForm, onRefetch]);

  const handleUploadSigned = useCallback(async () => {
    if (!signedFile) return;
    setSaving(true);
    try {
      const result = await handleClaimForm.mutateAsync({
        caseId: caseInfo.accidentCaseNo,
        type:   "SIGNED",
        file:   signedFile,
      });
      onRefetch?.({
        claimFormSignedDoc: result?.fileUrl || signedFile.name,
        currentStatus:      "CLAIM_FORM_SIGNED",
      });
      setPanel(null);
      setSignedFile(null);
    } catch (e) { console.error(e); }
    setSaving(false);
  }, [signedFile, caseInfo.accidentCaseNo, handleClaimForm, onRefetch]);

  const handleApprove = useCallback(async () => {
    setSaving(true);
    try {
      await handleClaimForm.mutateAsync({
        caseId: caseInfo.accidentCaseNo,
        type:   "APPROVE",
      });
      onRefetch?.({
        claimFormApprovedDate: new Date().toISOString(),
        currentStatus:         "CLAIM_FORM_APPROVED",
      });
    } catch (e) { console.error(e); }
    setSaving(false);
  }, [caseInfo.accidentCaseNo, handleClaimForm, onRefetch]);

  const handleSend = useCallback(async () => {
    setMailing(true);
    try {
      await handleClaimForm.mutateAsync({
        caseId: caseInfo.accidentCaseNo,
        type:   "SEND",
        note:   `Claim form sent to ${insuranceDetails?.company || "insurer"}`,
      });
      setMailed(true);
      onRefetch?.({
        claimFormSentDate: new Date().toISOString(),
        currentStatus:     "CLAIM_SUBMITTED",
      });
    } catch (e) { console.error(e); }
    setMailing(false);
  }, [caseInfo.accidentCaseNo, handleClaimForm, insuranceDetails, onRefetch]);

  const handleSkipClaimWorkflow = useCallback(async () => {
    setSaving(true);
    try {
      onRefetch?.({
        claimFormSentDate: new Date().toISOString(),
        currentStatus:     "CLAIM_SUBMITTED",
      });
      setSkipped(true);
    } catch (e) { console.error(e); }
    setSaving(false);
  }, [onRefetch]);

  // ── Digital insurance skip — no backend call, same as handleSkipClaimWorkflow ──
  const handleEnableDigitalInsurance = useCallback(() => {
    // Set local state first — this renders "skipped" immediately.
    // onRefetch updates caseInfo.currentStatus to CLAIM_SUBMITTED so the next
    // step unlocks. Both happen before the next render, and digitalInsurance
    // is checked first in the state ternary so "skipped" always wins over "done".
    setDigitalInsurance(true);
    onRefetch?.({
      claimFormSentDate: new Date().toISOString(),
      currentStatus:     "CLAIM_SUBMITTED",
    });
  }, [onRefetch]);

  const handleDisableDigitalInsurance = useCallback(() => {
    setDigitalInsurance(false);
    onRefetch?.({
      claimFormSentDate: null,
      currentStatus:     "ESTIMATE_APPROVED",
    });
  }, [onRefetch]);

  return (
    <StepCard state={state}>
      <StepHeader
        icon={
          digitalInsurance  ? "⚡"  :
          sentToInsurer     ? "✅"  :
          claimFormApproved ? "📤"  :
          customerSigned    ? "⚠️"  :
          claimFormUploaded ? "✍️"  : "📄"
        }
        title="Claim form"
        subtitle={
          digitalInsurance
            ? "Digit insurance — all claim form steps skipped"
            : sentToInsurer
            ? `Claim submitted to ${insuranceDetails?.company || "insurer"} on ${formatDate(caseInfo.claimFormSentDate)}`
            : claimFormApproved ? "Approved — ready to send to insurance company"
            : customerSigned    ? "Customer has signed — admin review required"
            : claimFormUploaded ? "Draft sent to customer for signature"
            : "Upload drafted claim form for customer to sign"
        }
        badge={
          digitalInsurance  ? "Skipped"           :
          sentToInsurer     ? "Submitted"          :
          claimFormApproved ? "Ready to send"      :
          customerSigned    ? "Pending approval"   :
          claimFormUploaded ? "Awaiting signature" :
          "Action required"
        }
        badgeColor={
          digitalInsurance  ? "var(--wd-purple, #a855f7)" :
          sentToInsurer     ? "var(--wd-green)"            :
          claimFormApproved ? "var(--wd-purple, #a855f7)"  :
          customerSigned    ? "var(--wd-orange)"           :
          "var(--wd-blue)"
        }
      />

      {/* ── Digital insurance toggle ─────────────────────────────────────────
          Show when: no standard flow started yet, OR digital is already on.
          Hide once customer has signed / standard flow is in progress.        */}
      {!claimFormUploaded && !customerSigned && !claimFormApproved && !sentToInsurer && !skipped && (
        <div style={{
          marginTop: 12,
          padding: "10px 12px",
          borderRadius: 10,
          background: digitalInsurance ? "rgba(168,85,247,.06)" : "var(--wd-hover)",
          border: digitalInsurance
            ? "1px solid rgba(168,85,247,.22)"
            : "1px solid var(--wd-border)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}>
          <div>
            <div style={{
              fontSize: 11, fontWeight: 700,
              color: digitalInsurance ? "var(--wd-purple, #a855f7)" : "var(--wd-t2)",
            }}>
              Insurance type
            </div>
            <div style={{ fontSize: 10, color: "var(--wd-t3)", marginTop: 1 }}>
              {digitalInsurance
                ? "All claim form steps are skipped"
                : "Switch to digit insurance to skip claim form steps"}
            </div>
          </div>

          {/* Segmented control */}
          <div style={{
            display: "inline-flex",
            borderRadius: 8,
            border: "1px solid var(--wd-border-md)",
            overflow: "hidden",
            flexShrink: 0,
          }}>
            <button
              onClick={handleDisableDigitalInsurance}
              style={{
                padding: "5px 13px",
                fontSize: 11, fontWeight: 600,
                border: "none",
                borderRight: "1px solid var(--wd-border-md)",
                cursor: "pointer",
                transition: "all .15s",
                background: !digitalInsurance ? "var(--wd-blue)" : "var(--wd-hover)",
                color:      !digitalInsurance ? "#fff"           : "var(--wd-t2)",
              }}
            >
              📄 Standard
            </button>
            <button
              onClick={handleEnableDigitalInsurance}
              style={{
                padding: "5px 13px",
                fontSize: 11, fontWeight: 600,
                border: "none",
                cursor: "pointer",
                transition: "all .15s",
                background: digitalInsurance ? "var(--wd-purple, #a855f7)" : "var(--wd-hover)",
                color:      digitalInsurance ? "#fff"                       : "var(--wd-t2)",
              }}
            >
              ⚡ Digit insurance
            </button>
          </div>
        </div>
      )}

      {/* ── Digital insurance skipped state ──────────────────────────────── */}
      {digitalInsurance && (
        <div style={{
          marginTop: 12, padding: "14px 16px", borderRadius: 10,
          background: "rgba(168,85,247,.06)", border: "1px solid rgba(168,85,247,.18)",
          display: "flex", alignItems: "flex-start", gap: 12,
        }}>
          <span style={{ fontSize: 20, flexShrink: 0 }}>⚡</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--wd-purple, #a855f7)", marginBottom: 6 }}>
              Digit insurance — all claim form steps skipped
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {[
                "Upload draft claim form",
                "Send to customer for signature",
                "Upload signed claim form",
                "Admin approval",
                "Send to insurer",
              ].map(step => (
                <div key={step} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11 }}>
                  <span style={{
                    width: 14, height: 14, borderRadius: 3, flexShrink: 0,
                    background: "rgba(168,85,247,.15)", border: "1px solid rgba(168,85,247,.2)",
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    fontSize: 9, color: "var(--wd-purple, #a855f7)",
                  }}>✓</span>
                  <span style={{ color: "var(--wd-t3)", textDecoration: "line-through", opacity: .65 }}>
                    {step}
                  </span>
                  <span style={{
                    fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 10,
                    background: "rgba(168,85,247,.1)", color: "var(--wd-purple, #a855f7)",
                    border: "1px solid rgba(168,85,247,.18)",
                  }}>
                    Skipped
                  </span>
                </div>
              ))}
            </div>
            <button
              className="wd-btn wd-btn-ghost wd-btn-sm"
              style={{ marginTop: 10, fontSize: 10 }}
              onClick={handleDisableDigitalInsurance}
            >
              ↩ Switch back to standard
            </button>
          </div>
        </div>
      )}

      {/* ── Everything below only renders for standard flow ───────────────── */}
      {!digitalInsurance && (
        <>
          {/* Skipped state */}
          {skipped && (
            <div style={{
              marginTop: 12, padding: "12px 14px", borderRadius: 10,
              background: "rgba(59,130,246,.07)", border: "1px solid rgba(59,130,246,.2)",
              display: "flex", alignItems: "center", gap: 12,
            }}>
              <span style={{ fontSize: 18 }}>⏭️</span>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--wd-blue)" }}>Claim workflow skipped</div>
                <div style={{ fontSize: 11, color: "var(--wd-t3)", marginTop: 1 }}>
                  All claim form steps have been skipped. Case marked as submitted.
                </div>
              </div>
            </div>
          )}

          {!skipped && (
            <>
              {/* Progress pills */}
              <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 12, flexWrap: "wrap" }}>
                {statusSteps.map((s, i) => (
                  <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{
                      fontSize: 11, fontWeight: 600, padding: "2px 10px", borderRadius: 20,
                      background: s.skipped ? "rgba(168,85,247,.10)"
                        : s.done  ? "rgba(34,197,94,.12)"
                        : "var(--wd-hover)",
                      color: s.skipped ? "var(--wd-purple, #a855f7)"
                        : s.done  ? "var(--wd-green)"
                        : "var(--wd-t3)",
                      border: `1px solid ${
                        s.skipped ? "rgba(168,85,247,.25)"
                        : s.done  ? "rgba(34,197,94,.25)"
                        : "var(--wd-border)"
                      }`,
                    }}>
                      {s.skipped ? "⊘ " : s.done ? "✓ " : ""}{s.label}
                    </span>
                    {i < statusSteps.length - 1 && (
                      <span style={{ fontSize: 11, color: "var(--wd-t3)" }}>→</span>
                    )}
                  </div>
                ))}
              </div>

              {/* Upload draft */}
              {!claimFormUploaded && panel !== "upload_draft" && (
                <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                  <button className="wd-btn wd-btn-primary wd-btn-sm"
                    onClick={() => setPanel("upload_draft")}>
                    📤 Upload draft claim form
                  </button>
                </div>
              )}

              {panel === "upload_draft" && (
                <div style={{
                  marginTop: 14, paddingTop: 14,
                  borderTop: "1px dashed var(--wd-border)",
                  display: "flex", flexDirection: "column", gap: 12,
                }}>
                  <div style={{ fontSize: 12, color: "var(--wd-t2)" }}>
                    Upload the pre-filled claim form PDF. Once uploaded, the customer will receive a link to sign and re-upload it.
                  </div>
                  <div
                    onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={e => {
                      e.preventDefault(); setDragOver(false);
                      const f = e.dataTransfer.files[0];
                      if (f?.type === "application/pdf") setDraftFile(f);
                    }}
                    style={{
                      border: `2px dashed ${dragOver ? "var(--wd-blue)" : draftFile ? "var(--wd-green)" : "var(--wd-border-md)"}`,
                      borderRadius: 10, padding: "20px 16px",
                      background: dragOver ? "rgba(59,130,246,.05)" : draftFile ? "rgba(34,197,94,.05)" : "var(--wd-hover)",
                      textAlign: "center", cursor: "pointer", transition: "all .15s",
                    }}
                    onClick={() => document.getElementById("claim-draft-input")?.click()}
                  >
                    <input id="claim-draft-input" type="file" accept="application/pdf"
                      style={{ display: "none" }}
                      onChange={e => { const f = e.target.files[0]; if (f?.type === "application/pdf") setDraftFile(f); }} />
                    {draftFile ? (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                        <span style={{ fontSize: 20 }}>📄</span>
                        <div style={{ textAlign: "left" }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--wd-green)" }}>{draftFile.name}</div>
                          <div style={{ fontSize: 11, color: "var(--wd-t3)" }}>{(draftFile.size / 1024).toFixed(0)} KB · PDF</div>
                        </div>
                        <button className="wd-btn wd-btn-ghost wd-btn-sm" style={{ fontSize: 11, marginLeft: 8 }}
                          onClick={e => { e.stopPropagation(); setDraftFile(null); }}>✕</button>
                      </div>
                    ) : (
                      <div>
                        <div style={{ fontSize: 20, marginBottom: 6 }}>📤</div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--wd-t2)" }}>Drop PDF here or click to browse</div>
                        <div style={{ fontSize: 11, color: "var(--wd-t3)", marginTop: 2 }}>Claim form draft — PDF only</div>
                      </div>
                    )}
                  </div>
                  <SaveRow onSave={handleUploadDraft} onCancel={() => setPanel(null)}
                    saving={saving} disabled={!draftFile} label="Upload & send to customer" />
                </div>
              )}

              {/* Awaiting customer signature */}
              {claimFormUploaded && !customerSigned && (
                <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
                  <div style={{
                    marginTop: 12, padding: "10px 14px", borderRadius: 10,
                    background: "rgba(59,130,246,.06)", border: "1px solid rgba(59,130,246,.18)",
                    display: "flex", alignItems: "center", gap: 12,
                  }}>
                    <span style={{ fontSize: 18 }}>⏳</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--wd-blue)" }}>Waiting for customer signature</div>
                      <div style={{ fontSize: 11, color: "var(--wd-t3)", marginTop: 1 }}>
                        Draft: <span style={{ fontFamily: "var(--wd-mono)" }}>{caseInfo.claimFormDoc || "—"}</span>
                      </div>
                    </div>
                    {panel !== "upload_signed" && (
                      <button className="wd-btn wd-btn-ghost wd-btn-sm" style={{ whiteSpace: "nowrap" }}
                        onClick={() => setPanel("upload_signed")}>
                        ✍️ Upload signed form
                      </button>
                    )}
                  </div>

                  {panel === "upload_signed" && (
                    <div style={{
                      padding: "14px", borderRadius: 10,
                      background: "rgba(249,115,22,.05)", border: "1px solid rgba(249,115,22,.2)",
                      display: "flex", flexDirection: "column", gap: 12,
                    }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--wd-orange)" }}>
                        ✍️ Upload signed claim form (admin)
                      </div>
                      <div style={{ fontSize: 11, color: "var(--wd-t2)", lineHeight: 1.5 }}>
                        Upload the physically signed form on behalf of the customer.
                        This will mark it as signed and move it to admin review.
                      </div>
                      <div
                        onDragOver={e => { e.preventDefault(); setSignedDragOver(true); }}
                        onDragLeave={() => setSignedDragOver(false)}
                        onDrop={e => {
                          e.preventDefault(); setSignedDragOver(false);
                          const f = e.dataTransfer.files[0];
                          if (f?.type === "application/pdf") setSignedFile(f);
                        }}
                        style={{
                          border: `2px dashed ${signedDragOver ? "var(--wd-orange)" : signedFile ? "var(--wd-green)" : "var(--wd-border-md)"}`,
                          borderRadius: 10, padding: "16px",
                          background: signedDragOver ? "rgba(249,115,22,.05)" : signedFile ? "rgba(34,197,94,.05)" : "var(--wd-hover)",
                          textAlign: "center", cursor: "pointer", transition: "all .15s",
                        }}
                        onClick={() => document.getElementById("signed-form-input")?.click()}
                      >
                        <input id="signed-form-input" type="file" accept="application/pdf"
                          style={{ display: "none" }}
                          onChange={e => { const f = e.target.files[0]; if (f?.type === "application/pdf") setSignedFile(f); }} />
                        {signedFile ? (
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                            <span style={{ fontSize: 18 }}>📄</span>
                            <div style={{ textAlign: "left" }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--wd-green)" }}>{signedFile.name}</div>
                              <div style={{ fontSize: 11, color: "var(--wd-t3)" }}>{(signedFile.size / 1024).toFixed(0)} KB · PDF</div>
                            </div>
                            <button className="wd-btn wd-btn-ghost wd-btn-sm" style={{ fontSize: 11, marginLeft: 8 }}
                              onClick={e => { e.stopPropagation(); setSignedFile(null); }}>✕</button>
                          </div>
                        ) : (
                          <div>
                            <div style={{ fontSize: 20, marginBottom: 6 }}>✍️</div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--wd-t2)" }}>
                              Drop signed PDF or click to browse
                            </div>
                            <div style={{ fontSize: 11, color: "var(--wd-t3)", marginTop: 2 }}>Signed claim form — PDF only</div>
                          </div>
                        )}
                      </div>
                      <SaveRow
                        onSave={handleUploadSigned}
                        onCancel={() => { setPanel(null); setSignedFile(null); }}
                        saving={saving}
                        disabled={!signedFile}
                        label="Upload signed form"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Customer signed — admin approve */}
              {customerSigned && !claimFormApproved && (
                <div style={{
                  marginTop: 12, padding: "12px 14px", borderRadius: 10,
                  background: "rgba(249,115,22,.07)", border: "1px solid rgba(249,115,22,.2)",
                  display: "flex", alignItems: "center", gap: 12,
                }}>
                  <span style={{ fontSize: 18 }}>✍️</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--wd-orange)" }}>Customer signed — review required</div>
                    <div style={{ fontSize: 11, color: "var(--wd-t3)", marginTop: 1 }}>
                      Signed doc: <span style={{ fontFamily: "var(--wd-mono)" }}>{caseInfo.claimFormDoc || "uploaded by customer"}</span>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    {caseInfo.claimFormDoc && (
                      <a href={caseInfo.claimFormDoc} target="_blank" rel="noopener noreferrer"
                        className="wd-btn wd-btn-ghost wd-btn-sm" style={{ fontSize: 11 }}>
                        View →
                      </a>
                    )}
                    <button className="wd-btn wd-btn-primary wd-btn-sm"
                      onClick={handleApprove} disabled={saving}>
                      {saving ? "Approving…" : "✓ Approve"}
                    </button>
                  </div>
                </div>
              )}

              {/* Approved — send to insurer */}
              {claimFormApproved && !sentToInsurer && (
                <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
                  <div style={{
                    padding: "12px 14px", borderRadius: 10,
                    background: "var(--wd-hover)", border: "1px solid var(--wd-border)",
                  }}>
                    <div style={{
                      fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                      letterSpacing: ".05em", color: "var(--wd-t3)", marginBottom: 8,
                    }}>
                      Will be sent to {insuranceDetails?.company || "Insurance Company"}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                      {[
                        ["Claim form",        caseInfo.claimFormDoc || "Signed PDF"],
                        ["Workshop",          workshopInfo?.name || "—"],
                        ["Workshop location", workshopInfo?.address || "—"],
                        ["Estimate amount",   formatINR(workshopInfo?.estimateAmount || caseInfo?.estimateAmount)],
                        ["Policy no",         caseInfo?.policyNo || "—"],
                        ["Claim no",          insuranceDetails?.claimNo || "—"],
                      ].map(([label, val]) => (
                        <div key={label}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--wd-t3)", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 2 }}>{label}</div>
                          <div style={{ fontSize: 12, color: "var(--wd-t1)", fontWeight: 500 }}>{val || "—"}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ flex: 1, fontSize: 12, color: "var(--wd-t3)" }}>
                      📧 To: <strong>{insuranceDetails?.email || caseInfo?.insuranceEmail || "insurance company"}</strong>
                    </div>
                    {mailed && <span style={{ fontSize: 12, color: "var(--wd-green)" }}>✓ Sent!</span>}
                    <button
                      className="wd-btn wd-btn-primary wd-btn-sm"
                      style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                      onClick={handleSend}
                      disabled={mailing || mailed}
                    >
                      <span>📤</span> {mailing ? "Sending…" : "Send to insurer"}
                    </button>
                  </div>
                </div>
              )}

              {/* Sent confirmation */}
              {sentToInsurer && (
                <div style={{
                  padding: "10px 14px", borderRadius: 10,
                  background: "rgba(34,197,94,.07)", border: "1px solid rgba(34,197,94,.2)",
                  display: "flex", alignItems: "center", gap: 10,
                }}>
                  <span style={{ fontSize: 16 }}>✅</span>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--wd-green)" }}>Claim submitted to insurer</div>
                    <div style={{ fontSize: 11, color: "var(--wd-t3)", marginTop: 1 }}>
                      Sent to {insuranceDetails?.company || "insurance company"} · Awaiting workshop surveyor assignment
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </StepCard>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STEP C — Assign Workshop Surveyor (after insurer responds)
// ─────────────────────────────────────────────────────────────────────────────
export function StepAssignWorkshopSurveyor({ caseInfo, workshopSurvey, onRefetch, onGoToSurveys, readOnly = false }) {
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  const surveyorAssigned = !!workshopSurvey?.surveyorId;
  const surveyorDone     = workshopSurvey?.status === "COMPLETED";

  const handleCopy = () => {
    navigator.clipboard.writeText(caseInfo?.accidentCaseNo || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (surveyorDone) {
    return (
      <StepCard state="done">
        <StepHeader
          icon="✅"
          title="Workshop Survey Completed"
          subtitle={`${workshopSurvey.surveyorName || workshopSurvey.surveyorId} · Completed ${formatDate(workshopSurvey.completedAt)}`}
          badge="Done"
          badgeColor="var(--wd-green)"
        />
      </StepCard>
    );
  }

  if (surveyorAssigned) {
    return (
      <StepCard state="info">
        <StepHeader
          icon="👤"
          title="Workshop Surveyor Assigned"
          subtitle={`${workshopSurvey.surveyorName || workshopSurvey.surveyorId} · ${workshopSurvey.status}`}
          badge="Assigned"
          badgeColor="var(--wd-purple, #a855f7)"
        />
        <div style={{
          marginTop: 12, padding: "10px 14px", borderRadius: 10,
          background: "var(--wd-hover)", border: "1px solid var(--wd-border)",
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10,
        }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--wd-t3)", marginBottom: 2 }}>Surveyor</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--wd-t1)" }}>{workshopSurvey.surveyorName || workshopSurvey.surveyorId}</div>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--wd-t3)", marginBottom: 2 }}>Scheduled</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--wd-t1)" }}>{formatDateTime(workshopSurvey.scheduledAt) || "—"}</div>
          </div>
        </div>
        <div style={{
          marginTop: 10, padding: "8px 12px", borderRadius: 8,
          background: "rgba(59,130,246,.06)", border: "1px solid rgba(59,130,246,.18)",
          fontSize: 11, color: "var(--wd-t2)", lineHeight: 1.6,
        }}>
          ℹ️ Surveyor assigned and scheduled. Mark the survey as complete in the Surveys tab once the inspection is done.
        </div>
        <button
          className="wd-btn wd-btn-primary"
          style={{ 
            marginTop: 10, width: "100%",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8 
          }}
          onClick={onGoToSurveys}
        >
          <span>📋</span>
          Go to Surveys Tab
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" 
            stroke="currentColor" strokeWidth="2.5">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </button>
      </StepCard>
    );
  }

  // Not assigned yet — show assign CTA
  return (
    <StepCard state="warning">
      <StepHeader
        icon="⏳"
        title="Assign Workshop Surveyor"
        subtitle="Insurer has responded — assign a workshop surveyor for final inspection"
        badge="Action Required"
        badgeColor="var(--wd-orange)"
      />

      <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ fontSize: 12, color: "var(--wd-t2)", lineHeight: 1.6 }}>
          Once the insurer provides surveyor details, assign them from the Surveyors page using the case ID below.
        </div>

        {/* Case ID copy row */}
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "10px 14px", borderRadius: 10,
          background: "var(--wd-hover)", border: "1px solid var(--wd-border)",
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--wd-t3)", marginBottom: 3 }}>Case ID</div>
            <div style={{ fontFamily: "var(--wd-mono)", fontSize: 15, fontWeight: 700, color: "var(--wd-blue)" }}>
              {caseInfo?.accidentCaseNo || "—"}
            </div>
          </div>
          <button
            className="wd-btn wd-btn-ghost wd-btn-sm"
            style={copied ? { color: "var(--wd-green)", borderColor: "var(--wd-green)" } : {}}
            onClick={handleCopy}
          >
            {copied ? "✓ Copied!" : "Copy ID"}
          </button>
        </div>

        <button
          className="wd-btn wd-btn-primary"
          style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
          onClick={() => navigate("/admin/surveyors", {
            state: {
              prefillCaseId: caseInfo?.accidentCaseNo,
              surveyType: "WORKSHOP_SURVEY",
              returnTo: `/admin/cases/${caseInfo?.accidentCaseNo}`,
              returnTab: "Workshop",
            }
          })}
        >
          <span>👤</span>
          Assign Workshop Surveyor
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </button>
      </div>
    </StepCard>
  );
}

export function StepAwaitWorkshopSurvey({ caseInfo, workshopSurvey, onGoToSurveys }) {
  const surveyDone = workshopSurvey?.status === "COMPLETED";

  if (surveyDone) {
    return (
      <StepCard state="done">
        <StepHeader
          icon="✅"
          title="Workshop Survey Completed"
          subtitle={`${workshopSurvey.surveyorName || workshopSurvey.surveyorId} · Completed`}
          badge="Done"
          badgeColor="var(--wd-green)"
        />
      </StepCard>
    );
  }

  return (
    <StepCard state="warning">
      <StepHeader
        icon="🔍"
        title="Workshop survey in progress"
        subtitle={`${workshopSurvey?.surveyorName || "Surveyor"} is assigned and scheduled — mark as done once inspection is complete`}
        badge="Awaiting completion"
        badgeColor="var(--wd-yellow)"
      />

      <div style={{
        marginTop: 12, padding: "12px 14px", borderRadius: 10,
        background: "var(--wd-hover)", border: "1px solid var(--wd-border)",
        display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10,
      }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", 
            letterSpacing: ".05em", color: "var(--wd-t3)", marginBottom: 2 }}>
            Surveyor
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--wd-t1)" }}>
            {workshopSurvey?.surveyorName || "—"}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", 
            letterSpacing: ".05em", color: "var(--wd-t3)", marginBottom: 2 }}>
            Scheduled
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--wd-t1)" }}>
            {workshopSurvey?.scheduledAt 
              ? new Date(workshopSurvey.scheduledAt).toLocaleString("en-IN", { 
                  day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" 
                })
              : "—"}
          </div>
        </div>
      </div>

      <div style={{
        marginTop: 10, padding: "8px 12px", borderRadius: 8,
        background: "rgba(234,179,8,.06)", border: "1px solid rgba(234,179,8,.18)",
        fontSize: 11, color: "var(--wd-t2)", lineHeight: 1.6,
      }}>
        ⚠️ Repair phase is blocked until this survey is marked complete.
        Go to the Surveys tab to update the status.
      </div>

      <button
        className="wd-btn wd-btn-primary"
        style={{ 
          marginTop: 10, width: "100%",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8 
        }}
        onClick={onGoToSurveys}
      >
        <span>📋</span>
        Go to Surveys tab
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" 
          stroke="currentColor" strokeWidth="2.5">
          <path d="M5 12h14M12 5l7 7-7 7"/>
        </svg>
      </button>
    </StepCard>
  );
}
